import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, CheckCircle2, Clock, XCircle, FileText, ArrowRight, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  submitted: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', label: 'Application Received', desc: 'Your application has been received and is in our queue for review.' },
  pending: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Under Review', desc: 'Our team is currently reviewing your application. You will be notified by email once a decision is made.' },
  approved: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', label: 'Approved', desc: 'Congratulations! Your application has been approved. Check your email for license transfer instructions and next steps.' },
  rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50 border-red-200', label: 'Not Approved', desc: 'Unfortunately, we were unable to approve your application at this time. Check your email for details and feedback.' },
};

export default function ApplicationStatus() {
  const [email, setEmail] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useSEO({
    title: 'Check Application Status | Krieger Real Estate',
    description: 'Check the status of your Krieger Real Estate agent application. Enter your email to see real-time updates.',
    canonical: 'https://joinkrieger.com/application-status',
  });

  const checkStatus = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    setResults(null);
    try {
      const res = await axios.get(`${API}/applications/status`, { params: { email: email.trim() } });
      setResults(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No application found for this email address. Please check the email or apply first.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#0F1729] text-white pt-28 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-blue-400 font-semibold mb-3">Application Tracker</p>
          <h1 className="text-3xl sm:text-4xl font-bold font-['Cabinet_Grotesk'] mb-4">
            Check Your Application Status
          </h1>
          <p className="text-base text-slate-400">
            Enter the email address you used when applying to see your real-time application status.
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 -mt-6">
        <form onSubmit={checkStatus} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="status-tracker-form">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="pl-10"
                data-testid="status-email-input"
                required
              />
            </div>
            <Button type="submit" disabled={loading} className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-lg px-6" data-testid="check-status-btn">
              {loading ? <Loader2 size={16} className="animate-spin" /> : 'Check Status'}
            </Button>
          </div>
        </form>

        {/* Error */}
        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-5 text-center" data-testid="status-error">
            <p className="text-sm text-red-600">{error}</p>
            <Link to="/apply" className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-[#002FA7] hover:underline">
              Apply Now <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {/* Results */}
        {results && results.length > 0 && (
          <div className="mt-6 space-y-4" data-testid="status-results">
            {results.map(app => {
              const config = STATUS_CONFIG[app.status] || STATUS_CONFIG.submitted;
              const Icon = config.icon;
              return (
                <div key={app.id} className={`rounded-xl border p-6 ${config.bg}`} data-testid={`status-card-${app.status}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0 ${config.color}`}>
                      <Icon size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h2 className="text-lg font-['Cabinet_Grotesk'] font-bold text-slate-900">{config.label}</h2>
                      </div>
                      <p className="text-sm text-slate-600 mb-4">{config.desc}</p>

                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-slate-400 block">Applicant</span>
                          <span className="text-slate-700 font-medium">{app.first_name} {app.last_name}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Location</span>
                          <span className="text-slate-700 font-medium">{app.preferred_location || '—'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Applied</span>
                          <span className="text-slate-700 font-medium">{formatDate(app.created_at)}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Last Updated</span>
                          <span className="text-slate-700 font-medium">{formatDate(app.updated_at)}</span>
                        </div>
                      </div>

                      {/* Timeline */}
                      <div className="mt-5 pt-4 border-t border-slate-200/60">
                        <div className="flex items-center gap-0">
                          {['submitted', 'pending', 'approved'].map((step, i) => {
                            const stepOrder = { submitted: 0, pending: 1, approved: 2, rejected: 2 };
                            const current = stepOrder[app.status] || 0;
                            const isActive = i <= current;
                            const isRejected = app.status === 'rejected' && i === 2;
                            return (
                              <React.Fragment key={step}>
                                {i > 0 && <div className={`flex-1 h-0.5 ${isActive ? (isRejected ? 'bg-red-300' : 'bg-emerald-400') : 'bg-slate-200'}`} />}
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${isActive ? (isRejected ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white') : 'bg-slate-200 text-slate-400'}`}>
                                  {isRejected ? '✕' : isActive ? '✓' : i + 1}
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-[9px] text-slate-400">Received</span>
                          <span className="text-[9px] text-slate-400">Review</span>
                          <span className="text-[9px] text-slate-400">{app.status === 'rejected' ? 'Decision' : 'Approved'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Help text */}
        <div className="mt-10 text-center pb-16">
          <p className="text-sm text-slate-400">Haven't applied yet?</p>
          <Link to="/apply" className="inline-flex items-center gap-1 mt-1 text-sm font-semibold text-[#002FA7] hover:underline" data-testid="apply-link">
            Apply Now <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
